import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { WhatsappLogo, ArrowClockwise } from "@phosphor-icons/react";
import { devDebug } from "@/lib/debug";
import TelegramWebhookPanel from "./whatsappSettings/TelegramWebhookPanel";
import WATestAndLogsPanel from "./whatsappSettings/WATestAndLogsPanel";
import WARecipientsPanel from "./whatsappSettings/WARecipientsPanel";
import WATemplatesPanel from "./whatsappSettings/WATemplatesPanel";

const PLACEHOLDER_LEGEND = [
  ["{no_spb}", "Nomor SPB"],
  ["{tanggal}", "Tanggal SPB (format DD-MM-YYYY)"],
  ["{no_polisi}", "No Polisi truk"],
  ["{nama_supir}", "Nama supir"],
  ["{produk_nama}", "Nama produk"],
  ["{jumlah}", "Jumlah"],
  ["{satuan}", "Satuan (TON/SAK)"],
  ["{distrik_kode}", "Kode distrik"],
  ["{distrik_nama}", "Nama distrik"],
  ["{plant}", "Plant"],
  ["{transporter}", "Transporter"],
  ["{no_so}", "No SO"],
  ["{nama_pelanggan}", "Nama pelanggan (tujuan)"],
  ["{alamat_pengiriman}", "Alamat pengiriman"],
];

const REMINDER_PLACEHOLDERS = [
  ["{no_spb}", "Nomor SPB"],
  ["{no_polisi}", "No Polisi"],
  ["{produk_nama}", "Nama produk"],
  ["{jumlah}", "Jumlah"],
  ["{satuan}", "Satuan"],
  ["{distrik_kode}", "Distrik"],
  ["{menit_menunggu}", "Menit sudah menunggu"],
];

const CANCEL_PLACEHOLDERS = [
  ["{no_spb}", "Nomor SPB"],
  ["{tanggal}", "Tanggal SPB"],
  ["{no_polisi}", "No Polisi"],
  ["{produk_nama}", "Nama produk"],
  ["{jumlah}", "Jumlah"],
  ["{satuan}", "Satuan"],
  ["{distrik_kode}", "Kode distrik"],
  ["{distrik_nama}", "Nama distrik"],
  ["{nama_pelanggan}", "Nama pelanggan"],
  ["{reason}", "Alasan pembatalan"],
  ["{actor}", "Yang membatalkan"],
];

const EDIT_PLACEHOLDERS = [
  ["{no_spb}", "Nomor SPB"],
  ["{tanggal}", "Tanggal SPB"],
  ["{no_polisi}", "No Polisi"],
  ["{distrik_kode}", "Kode distrik"],
  ["{distrik_nama}", "Nama distrik"],
  ["{nama_pelanggan}", "Nama pelanggan"],
  ["{changes_list}", "Daftar perubahan (auto-format)"],
  ["{actor}", "Yang mengedit"],
];

export default function WhatsAppSettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ count: 0, numbers: [] });
  const [testNumber, setTestNumber] = useState("");
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [retryQueue, setRetryQueue] = useState([]);
  const [retryStats, setRetryStats] = useState({ pending: 0, success: 0, permanent_failure: 0, total: 0 });
  const [retryFilter, setRetryFilter] = useState("pending"); // pending | permanent_failure | all
  const [newRecipientName, setNewRecipientName] = useState("");
  const [newRecipientNumber, setNewRecipientNumber] = useState("");
  const [runningReminder, setRunningReminder] = useState(false);
  // Per-recipient test-send state (idx of row currently testing + persisted results)
  const [testingRecipient, setTestingRecipient] = useState(null);
  const [recipientResults, setRecipientResults] = useState({});

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: p }, { data: l }, { data: rs }] = await Promise.all([
        api.get("/whatsapp/settings"),
        api.get("/whatsapp/recipients/preview"),
        api.get("/whatsapp/logs", { params: { limit: 30 } }),
        api.get("/whatsapp/retry-queue/stats"),
      ]);
      setForm({
        enabled: !!s.enabled,
        provider: s.provider || "fonnte",
        fonnte_token: "",
        fonnte_token_set: !!s.fonnte_token_set,
        twilio_sid: s.twilio_sid || "",
        twilio_token: "",
        twilio_token_set: !!s.twilio_token_set,
        twilio_from: s.twilio_from || "",
        telegram_bot_token: "",
        telegram_bot_token_set: !!s.telegram_bot_token_set,
        telegram_admin_chat_ids: (s.telegram_admin_chat_ids || []).join("\n"),
        template: s.template || "",
        cancel_template: s.cancel_template || "",
        edit_template: s.edit_template || "",
        extra_recipients: s.extra_recipients || [],
        notify_petugas: s.notify_petugas !== false,
        reminder_enabled: !!s.reminder_enabled,
        reminder_after_minutes: s.reminder_after_minutes || 30,
        reminder_template: s.reminder_template || "",
      });
      setPreview(p);
      setLogs(l || []);
      setRetryStats(rs || { pending: 0, success: 0, permanent_failure: 0, total: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // Bootstrap-once. `loadAll` uses stable setters + module-scope api.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload retry queue whenever filter changes. Placed BEFORE any early
  // return so hook order stays consistent across renders.
  useEffect(() => {
    if (loading) return;
    // Inline the loader to avoid using `loadRetryQueue` closure (declared below).
    (async () => {
      try {
        const params = retryFilter === "all" ? {} : { status: retryFilter };
        const [{ data: q }, { data: s }] = await Promise.all([
          api.get("/whatsapp/retry-queue", { params: { ...params, limit: 100 } }),
          api.get("/whatsapp/retry-queue/stats"),
        ]);
        setRetryQueue(q || []);
        setRetryStats(s || { pending: 0, success: 0, permanent_failure: 0, total: 0 });
      } catch (err) {
        // Non-fatal — retry-queue widget is secondary. User can hit refresh.
        // Log in dev builds so failures aren't completely silent when debugging.
        if (process.env.NODE_ENV === "development") console.warn("retry-queue fetch failed:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryFilter, loading]);

  if (loading || !form) {
    return <div className="p-6 text-muted-foreground text-sm">Memuat pengaturan WhatsApp...</div>;
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addRecipient = () => {
    if (!newRecipientNumber.trim()) return;
    setField("extra_recipients", [
      ...form.extra_recipients,
      { name: newRecipientName.trim(), number: newRecipientNumber.trim() },
    ]);
    setNewRecipientName("");
    setNewRecipientNumber("");
  };

  const removeRecipient = (idx) => {
    setField("extra_recipients", form.extra_recipients.filter((_, i) => i !== idx));
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        provider: form.provider,
        template: form.template,
        cancel_template: form.cancel_template,
        edit_template: form.edit_template,
        extra_recipients: form.extra_recipients,
        notify_petugas: form.notify_petugas,
        twilio_sid: form.twilio_sid,
        twilio_from: form.twilio_from,
        reminder_enabled: form.reminder_enabled,
        reminder_after_minutes: form.reminder_after_minutes,
        reminder_template: form.reminder_template,
      };
      // Only send tokens if user actually entered new value
      if (form.fonnte_token) payload.fonnte_token = form.fonnte_token;
      if (form.twilio_token) payload.twilio_token = form.twilio_token;
      if (form.telegram_bot_token) payload.telegram_bot_token = form.telegram_bot_token;
      // Split whitelist textarea into array of chat_ids
      payload.telegram_admin_chat_ids = (form.telegram_admin_chat_ids || "")
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await api.put("/whatsapp/settings", payload);
      toast.success("Pengaturan WhatsApp tersimpan");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!testNumber.trim()) {
      toast.error("Masukkan nomor tujuan test");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/whatsapp/test", { number: testNumber.trim() });
      if (data.ok) toast.success("Test terkirim — cek WhatsApp");
      else toast.error(`Gagal: ${data.response || "unknown error"}`);
      // refresh logs
      const { data: l } = await api.get("/whatsapp/logs", { params: { limit: 30 } });
      setLogs(l || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  // Per-recipient test-send. `testingRecipient` tracks which row is loading,
  // `recipientResults[idx]` remembers pass/fail so the row can render a badge.
  const testRecipient = async (idx, number) => {
    if (!number || !String(number).trim()) {
      toast.error("Nomor kosong");
      return;
    }
    setTestingRecipient(idx);
    try {
      const { data } = await api.post("/whatsapp/test", { number: String(number).trim() });
      setRecipientResults((prev) => ({
        ...prev,
        [idx]: {
          ok: !!data.ok,
          response: (data.response || "").slice(0, 200),
          at: Date.now(),
        },
      }));
      if (data.ok) toast.success(`Test ke ${number} berhasil`);
      else toast.error(`Gagal ke ${number}: ${(data.response || "unknown").slice(0, 120)}`);
      // refresh logs so admin can inspect failure detail
      const { data: l } = await api.get("/whatsapp/logs", { params: { limit: 30 } });
      setLogs(l || []);
    } catch (err) {
      const msg = formatApiError(err);
      setRecipientResults((prev) => ({ ...prev, [idx]: { ok: false, response: msg, at: Date.now() } }));
      toast.error(msg);
    } finally {
      setTestingRecipient(null);
    }
  };

  const onRunReminders = async () => {
    setRunningReminder(true);
    try {
      const { data } = await api.post("/whatsapp/reminders/run");
      toast.success(`Reminder scan: ${data.sent} terkirim / ${data.scanned} kandidat`);
      const { data: l } = await api.get("/whatsapp/logs", { params: { limit: 30 } });
      setLogs(l || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunningReminder(false);
    }
  };

  // ─── Retry queue ─────────────────────────────────────────────────
  const loadRetryQueue = async () => {
    try {
      const params = retryFilter === "all" ? {} : { status: retryFilter };
      const [{ data: q }, { data: s }] = await Promise.all([
        api.get("/whatsapp/retry-queue", { params: { ...params, limit: 100 } }),
        api.get("/whatsapp/retry-queue/stats"),
      ]);
      setRetryQueue(q || []);
      setRetryStats(s || retryStats);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onForceRetry = async (qid) => {
    try {
      const { data } = await api.post(`/whatsapp/retry-queue/${qid}/retry`);
      if (data.counters?.success > 0) {
        toast.success("Retry berhasil terkirim!");
      } else {
        toast.error("Retry masih gagal. Cek log detail.");
      }
      await loadRetryQueue();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDeleteRetryItem = async (qid) => {
    if (!window.confirm("Hapus item ini dari retry queue?")) return;
    try {
      await api.delete(`/whatsapp/retry-queue/${qid}`);
      toast.success("Item dihapus dari queue");
      await loadRetryQueue();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="border-b border-border pb-6 mb-6">
        <div className="overline">Notifikasi</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
            <WhatsappLogo size={22} weight="bold" />
          </span>
          Notifikasi (WhatsApp / Telegram)
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Kirim notifikasi otomatis ke Petugas Lapangan tiap SPB baru dibuat. Pilih channel
          (Fonnte / Twilio untuk WhatsApp, atau Telegram Bot), isi kredensial, tambahkan penerima, dan atur template pesan.
        </p>
      </div>

      <form onSubmit={onSave} className="space-y-8" data-testid="wa-settings-form">
        {/* Master toggle + provider */}
        <section className="border border-border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-bold text-sm">Aktifkan Notifikasi WA</div>
              <div className="text-xs text-muted-foreground">
                Saat aktif, setiap SPB baru akan otomatis mengirim WA ke penerima yang terdaftar.
              </div>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setField("enabled", v)}
              data-testid="wa-enabled-switch"
            />
          </div>
          <div>
            <Label className="overline block mb-2">Provider *</Label>
            <Select value={form.provider} onValueChange={(v) => setField("provider", v)}>
              <SelectTrigger className="rounded-xl h-11 max-w-xs" data-testid="wa-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fonnte">Fonnte (WhatsApp Indonesia)</SelectItem>
                <SelectItem value="twilio">Twilio (WhatsApp Global)</SelectItem>
                <SelectItem value="telegram">Telegram Bot</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Credentials */}
        {form.provider === "fonnte" ? (
          <section className="border border-border bg-card p-5 space-y-4">
            <div className="overline">Kredensial Fonnte</div>
            <div>
              <Label className="text-sm mb-2 block">Token Device Fonnte</Label>
              <Input
                type="password"
                value={form.fonnte_token}
                onChange={(e) => setField("fonnte_token", e.target.value)}
                placeholder={form.fonnte_token_set ? "•••••• (tersimpan — kosongkan berarti tetap)" : "Paste token dari dashboard Fonnte"}
                className="rounded-xl h-11 font-mono"
                data-testid="wa-fonnte-token"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Dapatkan token di <span className="font-mono">fonnte.com → Device → Token</span>. Pastikan device WA sudah terkoneksi.
              </p>
            </div>
          </section>
        ) : form.provider === "twilio" ? (
          <section className="border border-border bg-card p-5 space-y-4">
            <div className="overline">Kredensial Twilio</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-2 block">Account SID</Label>
                <Input
                  value={form.twilio_sid}
                  onChange={(e) => setField("twilio_sid", e.target.value)}
                  placeholder="ACxxxxxxxxxxxx"
                  className="rounded-xl h-11 font-mono"
                  data-testid="wa-twilio-sid"
                />
              </div>
              <div>
                <Label className="text-sm mb-2 block">Auth Token</Label>
                <Input
                  type="password"
                  value={form.twilio_token}
                  onChange={(e) => setField("twilio_token", e.target.value)}
                  placeholder={form.twilio_token_set ? "•••••• (tersimpan)" : "Paste auth token"}
                  className="rounded-xl h-11 font-mono"
                  data-testid="wa-twilio-token"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm mb-2 block">From Number (WhatsApp)</Label>
                <Input
                  value={form.twilio_from}
                  onChange={(e) => setField("twilio_from", e.target.value)}
                  placeholder="whatsapp:+14155238886 (sandbox) atau +62..."
                  className="rounded-xl h-11 font-mono"
                  data-testid="wa-twilio-from"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Untuk sandbox: <code>whatsapp:+14155238886</code>. Untuk produksi: nomor WA Business yang sudah di-approve.
                </p>
              </div>
            </div>
          </section>
        ) : (
          <section className="border border-border bg-card p-5 space-y-4" data-testid="wa-telegram-section">
            <div className="overline">Kredensial Telegram Bot</div>
            <div>
              <Label className="text-sm mb-2 block">Bot Token</Label>
              <Input
                type="password"
                value={form.telegram_bot_token}
                onChange={(e) => setField("telegram_bot_token", e.target.value)}
                placeholder={form.telegram_bot_token_set ? "•••••• (tersimpan — kosongkan berarti tetap)" : "Contoh: 123456789:AAE-xxxxxxxx..."}
                className="rounded-xl h-11 font-mono"
                data-testid="wa-telegram-token"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Chat ke <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="underline text-primary">@BotFather</a> di Telegram → kirim <code>/newbot</code> → ikuti wizard → salin token.
              </p>
            </div>
            <div className="text-xs bg-secondary/50 border-l-4 border-primary p-3 space-y-1">
              <div className="font-bold">📋 Cara Kirim ke Grup Telegram</div>
              <ol className="list-decimal ml-4 space-y-0.5 text-muted-foreground">
                <li>Add bot ke grup target dan jadikan admin (agar bisa kirim).</li>
                <li>Ketik apa saja di grup lalu buka: <code className="text-foreground">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code></li>
                <li>Cari field <code className="text-foreground">chat.id</code> (biasanya negatif, mis. <code className="text-foreground">-1001234567890</code>).</li>
                <li>Paste chat_id itu di section <b>Penerima</b> (bukan nomor 08xxx).</li>
              </ol>
            </div>
            <TelegramWebhookPanel />
            <div>
              <Label className="text-sm mb-2 block">Whitelist Chat ID Admin (untuk command tulis-data)</Label>
              <Textarea
                value={form.telegram_admin_chat_ids}
                onChange={(e) => setField("telegram_admin_chat_ids", e.target.value)}
                placeholder={"Satu chat_id per baris, mis:\n123456789\n-1001234567890"}
                rows={3}
                className="rounded-xl font-mono text-sm"
                data-testid="wa-telegram-admin-chats"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Hanya chat_id di daftar ini yang boleh menjalankan <code>/spb_baru</code> dan <code>/spb_batal</code>.
                Chat_id pribadi Anda muncul saat Anda kirim <code>/help</code> ke bot — cek log Telegram atau <code>getUpdates</code>.
              </p>
            </div>
          </section>
        )}

        <WARecipientsPanel
          form={form}
          setField={setField}
          previewCount={preview.count}
          newRecipientName={newRecipientName}
          setNewRecipientName={setNewRecipientName}
          newRecipientNumber={newRecipientNumber}
          setNewRecipientNumber={setNewRecipientNumber}
          addRecipient={addRecipient}
          removeRecipient={removeRecipient}
          recipientResults={recipientResults}
          testingRecipient={testingRecipient}
          testRecipient={testRecipient}
        />

        <WATemplatesPanel
          form={form}
          setField={setField}
          onRunReminders={onRunReminders}
          runningReminder={runningReminder}
          placeholders={PLACEHOLDER_LEGEND}
          reminderPlaceholders={REMINDER_PLACEHOLDERS}
          cancelPlaceholders={CANCEL_PLACEHOLDERS}
          editPlaceholders={EDIT_PLACEHOLDERS}
        />

        <div className="flex flex-wrap gap-3">
          <Button
            type="submit"
            disabled={saving}
            className="rounded-xl font-bold uppercase"
            data-testid="wa-save-button"
          >
            {saving ? "Menyimpan..." : "Simpan Pengaturan"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={loadAll}
            data-testid="wa-refresh-button"
          >
            <ArrowClockwise size={16} className="mr-1" /> Refresh
          </Button>
        </div>
      </form>

      <WATestAndLogsPanel
        provider={form.provider}
        testNumber={testNumber}
        setTestNumber={setTestNumber}
        testing={testing}
        onTest={onTest}
        retryQueue={retryQueue}
        retryStats={retryStats}
        retryFilter={retryFilter}
        setRetryFilter={setRetryFilter}
        onRefreshQueue={loadRetryQueue}
        onForceRetry={onForceRetry}
        onDeleteRetryItem={onDeleteRetryItem}
        logs={logs}
        onRefreshLogs={loadAll}
      />
    </div>
  );
}
